(function () {
  // The wrapper may hold two players (a landscape one for desktop and a
  // vertical / square cut for phones, swapped by CSS under 749px). The shared
  // sound + popup controls must act on whichever one is currently visible.
  var activePlayer = (wrapper) => {
    var players = wrapper.querySelectorAll(".animated-video__player");
    if (players.length < 2) return players[0] || null;
    var isMobile = window.matchMedia("(max-width: 749px)").matches;
    return (
      wrapper.querySelector(
        isMobile
          ? ".animated-video__player--mobile"
          : ".animated-video__player--desktop"
      ) || players[0]
    );
  };

  // --- Scroll-driven reveal (desktop only) ---------------------------------
  var videoScrollAnimated = () => {
    // On mobile the video is shown as a plain full-screen element (see CSS),
    // so skip the scroll-driven reveal animation entirely.
    if (window.matchMedia("(max-width: 749px)").matches) return;
    if (typeof gsap === "undefined") return;

    gsap.to(".animated-video__player", {
      scrollTrigger: {
        trigger: ".animated-video__wrapper",
        start: "top top",
        end: "bottom bottom",
        scrub: 0.5,
      },
      clipPath: "polygon(0% 0%, 0% 100%, 100% 100%, 100% 0%, 0% 0%)",
    });
  };

  // --- Click-to-play-with-sound toggle -------------------------------------
  // The video autoplays muted (required for autoplay). The button lets the
  // visitor turn sound on; the first click docks it into the corner as a
  // compact mute / unmute toggle. Sound just unmutes — playback continues
  // from wherever it is.
  var initSoundToggle = (root) => {
    var scope = root || document;
    var wrappers = scope.querySelectorAll(".animated-video__wrapper");

    wrappers.forEach((wrapper) => {
      var video = activePlayer(wrapper);
      var btn = wrapper.querySelector("[data-sound-toggle]");
      if (!video || !btn || btn.dataset.bound === "true") return;
      btn.dataset.bound = "true";

      // Reveal the button once the video can actually play.
      var reveal = () => btn.classList.add("is-ready");
      if (video.readyState >= 2) {
        reveal();
      } else {
        video.addEventListener("loadeddata", reveal, { once: true });
        video.addEventListener("canplay", reveal, { once: true });
      }
      // Safety net in case the media events fire before we attach (or never).
      setTimeout(reveal, 1200);

      var sync = (player) => {
        var soundOn = !player.muted;
        var docked = btn.classList.contains("is-active");
        btn.classList.toggle("is-on", soundOn);
        btn.setAttribute("aria-pressed", soundOn ? "true" : "false");
        btn.setAttribute(
          "aria-label",
          soundOn ? "Mute video" : docked ? "Unmute video" : "Play video with sound"
        );
      };

      btn.addEventListener("click", () => {
        // Resolve the visible player at click time so the control still works
        // after a resize / rotation swaps the desktop and mobile videos.
        var player = activePlayer(wrapper);
        if (!player) return;

        // First click docks the button into the corner.
        btn.classList.add("is-active");

        player.muted = !player.muted;
        if (!player.muted) {
          player.volume = 1;
          var playback = player.play();
          if (playback && typeof playback.catch === "function") {
            playback.catch(() => {});
          }
        }
        sync(player);
      });
    });
  };

  // --- Click-to-open popup player ------------------------------------------
  // The whole video (and the hint button) opens a full-screen <dialog> player
  // with native controls. The player markup lives in a <template> and is only
  // cloned in on open, then removed on close so it stops playing. The looping
  // background video is paused while the popup is open to avoid double audio.
  var initVideoModal = (root) => {
    var scope = root || document;
    var wrappers = scope.querySelectorAll(".animated-video__wrapper");

    wrappers.forEach((wrapper) => {
      var dialog = wrapper.querySelector("[data-video-modal]");
      if (!dialog || dialog.dataset.bound === "true") return;
      dialog.dataset.bound = "true";

      var template = dialog.querySelector("[data-video-template]");
      var stage = dialog.querySelector("[data-video-stage]");
      var triggers = wrapper.querySelectorAll("[data-video-open]");
      var bgState = null;

      var open = () => {
        if (dialog.open) return;

        if (template && stage && !stage.firstChild) {
          stage.appendChild(template.content.cloneNode(true));
        }

        // Pause the visible background video so its audio can't overlap the
        // popup (resolved live so it works after a desktop/mobile swap).
        var bgVideo = activePlayer(wrapper);
        if (bgVideo) {
          bgState = { el: bgVideo, muted: bgVideo.muted, playing: !bgVideo.paused };
          try { bgVideo.pause(); } catch (e) {}
        }

        // showModal() opens a top-layer, inert-background modal whose opaque
        // backdrop covers the page — so we deliberately do NOT lock <html>
        // scroll. Setting overflow:hidden on the root here corrupts the
        // section's position:sticky reveal and leaves white space on close.
        if (typeof dialog.showModal === "function") {
          dialog.showModal();
        } else {
          dialog.setAttribute("open", "");
        }

        // Start playback (with sound — this runs inside the click gesture).
        var media = stage && stage.querySelector("video");
        if (media) {
          media.muted = false;
          var playback = media.play();
          if (playback && typeof playback.catch === "function") {
            playback.catch(() => {});
          }
        }
      };

      var cleanup = () => {
        if (stage) stage.innerHTML = ""; // stop and unload the <video> / <iframe>

        if (bgState && bgState.el) {
          var bgVideo = bgState.el;
          bgVideo.muted = bgState.muted;
          if (bgState.playing) {
            var playback = bgVideo.play();
            if (playback && typeof playback.catch === "function") {
              playback.catch(() => {});
            }
          }
          bgState = null;
        }
      };

      var close = () => {
        if (dialog.open && typeof dialog.close === "function") {
          dialog.close(); // fires the "close" event -> cleanup
        } else {
          dialog.removeAttribute("open");
          cleanup();
        }
      };

      triggers.forEach((btn) => btn.addEventListener("click", open));
      wrapper
        .querySelectorAll(".animated-video__player")
        .forEach((player) => player.addEventListener("click", open));

      dialog
        .querySelectorAll("[data-video-close]")
        .forEach((btn) => btn.addEventListener("click", close));

      // Click on the dim backdrop (the dialog itself, outside the frame).
      dialog.addEventListener("click", (event) => {
        if (event.target === dialog) close();
      });

      // ESC key and dialog.close() both end here.
      dialog.addEventListener("close", cleanup);
    });
  };

  // --- Responsive playback -------------------------------------------------
  // With two players only one is visible at a time; pause the hidden one so it
  // doesn't keep decoding off-screen, and re-evaluate when the breakpoint is
  // crossed (resize / rotation).
  var manageResponsivePlayback = (root) => {
    var scope = root || document;
    scope.querySelectorAll(".animated-video__wrapper").forEach((wrapper) => {
      var players = wrapper.querySelectorAll(".animated-video__player");
      if (players.length < 2) return;

      var apply = () => {
        var active = activePlayer(wrapper);
        players.forEach((player) => {
          if (player === active) {
            var playback = player.play();
            if (playback && typeof playback.catch === "function") {
              playback.catch(() => {});
            }
          } else {
            try { player.pause(); } catch (e) {}
          }
        });
      };

      apply();

      if (wrapper.dataset.respBound !== "true") {
        wrapper.dataset.respBound = "true";
        var mq = window.matchMedia("(max-width: 749px)");
        if (mq.addEventListener) {
          mq.addEventListener("change", apply);
        } else if (mq.addListener) {
          mq.addListener(apply);
        }
      }
    });
  };

  // --- Boot ----------------------------------------------------------------
  document.addEventListener("shopify:section:load", function (event) {
    var target = event && event.target ? event.target : document;
    videoScrollAnimated();
    initSoundToggle(target);
    initVideoModal(target);
    manageResponsivePlayback(target);
  });

  if (document.readyState !== "loading") {
    initSoundToggle(document);
    initVideoModal(document);
    manageResponsivePlayback(document);
  } else {
    document.addEventListener("DOMContentLoaded", () => {
      initSoundToggle(document);
      initVideoModal(document);
      manageResponsivePlayback(document);
    });
  }

  setTimeout(() => {
    videoScrollAnimated();
  }, 800);
})();
